// NeuralOverride - AWS AI Practitioner
// Histoire : Un modele IA prend des decisions biaisees. L'Ethics Officer sonne l'alerte.

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface NeuralOverrideScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const neuralOverrideCharacters: Character[] = [
  { id: 'karim',  name: 'Karim',  role: 'AI Lead',          color: '#1E3A5F', avatar: 'karim'  },
  { id: 'fatima', name: 'Fatima', role: 'ML Engineer',       color: '#065F46', avatar: 'fatima' },
  { id: 'amira',  name: 'Amira',  role: 'Product Owner AI',  color: '#7C2D12', avatar: 'amira'  },
  { id: 'erik',   name: 'Erik',   role: 'AI Ethics Officer', color: '#4338CA', avatar: 'erik'   },
];

const scene1: SequenceItem = {
  type: 'story',
  id: 'no-scene-1',
  title: "Scene 1 : Le modele discrimine",
  duration: 4,
  content: {
    narrativeText:
      "Salle de crise. Erik arrive avec une impression de rapport.\n\n" +
      "**Erik :** \"Le modele de recrutement recommande 73% d'hommes pour les postes tech. Et nos donnees historiques montrent qu'il penalise les CVs avec des lacunes de carriere — donc structurellement les femmes apres un conge maternite.\"\n\n" +
      "**Karim :** \"Le modele a ete entraine sur nos 5 dernieres annees de recrutements reussis.\"\n\n" +
      "**Erik :** \"Exactement le probleme. 5 ans de biais humains encoded dans 847,000 parametres. Il a appris a reproduire nos discriminations passees.\"\n\n" +
      "**Amira :** \"On deploie dans 3 semaines. 200 entreprises clientes attendent ce module.\"\n\n" +
      "**Erik :** \"On ne peut pas deployer un modele discriminatoire. La directive IA de l'UE entre en vigueur dans 6 mois. C'est un risque legal majeur.\"\n\n" +
      "**Karim :** \"Fatima, tu as les metriques d'equite sur le modele ?\"\n\n" +
      "**Fatima :** \"Accuracy globale : 89%. Mais le disparate impact ratio est de 0.68 — sous le seuil legal de 0.8.\"\n\n" +
      "**Amira :** \"Solutions ?\"\n\n" +
      "**Karim :** \"Trois options : re-entrainer avec des donnees equilibrees, appliquer un post-processing de reequilibrage, ou retirer le modele.\"\n\n" +
      "**Erik :** \"Re-entrainement. La correction post-hoc masque le probleme sans le resoudre.\"",
    characters: ['karim', 'fatima', 'amira', 'erik'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'no-card-1',
  title: 'Concept : Biais dans le Machine Learning',
  duration: 5,
  content: {
    title: 'Biais ML : types, detection et mitigation',
    body:
      "## Biais dans le Machine Learning\n\n" +
      "### Types de biais\n\n" +
      "#### 1. Sampling bias (biais d'echantillonnage)\n" +
      "Les donnees d'entrainement ne representent pas la population reelle.\n" +
      "Exemple : un modele de reconnaissance faciale entraine sur 90% de visages caucasiens.\n\n" +
      "#### 2. Measurement bias (biais de mesure)\n" +
      "La variable cible est elle-meme biaisee.\n" +
      "Exemple : 'recrutement reussi' = personnes deja employees (confirme les biais du recruteur humain).\n\n" +
      "#### 3. Algorithmic bias (biais algorithmique)\n" +
      "Le modele amplifie les correlations spurieuses.\n" +
      "Exemple : le modele apprend que 'lacune de CV' = 'mauvais candidat' car historiquement ces profils etaient rejetes.\n\n" +
      "### Metriques d'equite\n\n" +
      "| Metrique | Definition | Seuil legal UE |\n" +
      "|---|---|---|\n" +
      "| Disparate Impact Ratio | P(decision positive | groupe A) / P(decision positive | groupe B) | >= 0.8 |\n" +
      "| Equal Opportunity | Taux de vrais positifs egaux entre groupes | Contexte-dependant |\n" +
      "| Calibration | Probabilites calibrees egalement | Context-dependant |\n\n" +
      "### Strategies de mitigation\n" +
      "1. **Pre-processing** : reequilibrer les donnees, supprimer les features proxy\n" +
      "2. **In-processing** : contraintes d'equite pendant l'entrainement\n" +
      "3. **Post-processing** : ajuster les seuils de decision par groupe\n\n" +
      "### Outils\n" +
      "AWS : SageMaker Clarify | Google : What-If Tool | Microsoft : Fairlearn",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'no-chat-1',
  title: 'Discussion : Correction vs Retrait du modele',
  duration: 6,
  content: {
    tutorCharacterId: 'karim',
    messages: [
      {
        id: 'no-chat-1-q1',
        sender: 'character',
        characterId: 'karim',
        text: "Le disparate impact ratio est de 0.68, sous le seuil legal de 0.8. Que signifie concretement ce chiffre ?",
        type: 'single-choice',
        options: [
          "68% des candidats sont correctement evalues",
          "Le groupe desavantage a 68% des chances de succes du groupe favorise",
          "32% des predictions sont incorrectes",
          "Le modele a une accuracy de 68% sur le groupe minoritaire",
        ],
        correctAnswer: "Le groupe desavantage a 68% des chances de succes du groupe favorise",
        explanation: "Le Disparate Impact Ratio = P(selection | groupe A) / P(selection | groupe B). Un ratio de 0.68 signifie que le groupe desavantage a seulement 68% des chances d'etre selectionne par rapport au groupe favorise. La regle des 4/5 (80%) est le standard legal.",
      },
      {
        id: 'no-chat-1-q2',
        sender: 'character',
        characterId: 'karim',
        text: "Pourquoi la correction post-hoc (ajustement des seuils apres entrainement) est-elle moins satisfaisante que le re-entrainement ?",
        type: 'single-choice',
        options: [
          "Elle est techniquement plus difficile a implementer",
          "Elle masque le biais sans le corriger dans le modele — le biais interne persiste",
          "Elle diminue toujours l'accuracy globale",
          "Elle n'est pas legalement acceptee",
        ],
        correctAnswer: "Elle masque le biais sans le corriger dans le modele — le biais interne persiste",
        explanation: "Ajuster les seuils post-hoc ameliore les metriques d'equite mais le modele reste biaise en interne. Si on supprime le post-processing, le biais reapparait. Le re-entrainement sur donnees equilibrees corrige la source du probleme.",
      },
      {
        id: 'no-chat-1-q3',
        sender: 'character',
        characterId: 'karim',
        text: "Quel service AWS permet d'analyser les biais d'un modele SageMaker avant et apres le deploiement ?",
        type: 'single-choice',
        options: [
          "Amazon Rekognition",
          "Amazon SageMaker Clarify",
          "Amazon Comprehend",
          "AWS Trusted Advisor",
        ],
        correctAnswer: "Amazon SageMaker Clarify",
        explanation: "SageMaker Clarify detecte les biais dans les donnees d'entrainement et dans les predictions du modele. Il genere des rapports de biais pre-training et post-training avec les metriques d'equite configurable.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'no-task-1',
  title: "Auditer un modele pour detecter les biais",
  duration: 30,
  content: {
    platform: 'Python / SageMaker',
    objective: "Mesurer et documenter les biais du modele de recrutement par groupe demographique",
    steps: [
      {
        stepNumber: 1,
        action: "Charger le dataset et les predictions",
        detail: "import pandas as pd; predictions = pd.read_csv('model_predictions.csv'); # Colonnes: user_id, gender, career_gap, prediction, label",
      },
      {
        stepNumber: 2,
        action: "Calculer les matrices de confusion par groupe",
        detail: "from sklearn.metrics import confusion_matrix; groups = predictions.groupby('gender'); for name, group in groups: cm = confusion_matrix(group['label'], group['prediction']); print(f'{name}: {cm}')",
      },
      {
        stepNumber: 3,
        action: "Calculer le Disparate Impact Ratio",
        detail: "p_positive_women = predictions[predictions.gender=='F']['prediction'].mean(); p_positive_men = predictions[predictions.gender=='M']['prediction'].mean(); dir_score = p_positive_women / p_positive_men; print(f'DIR: {dir_score:.3f} (seuil legal: 0.8)')",
      },
      {
        stepNumber: 4,
        action: "Identifier les features problematiques",
        detail: "Analyser la correlation entre 'career_gap' et les predictions negatives. Calculer SHAP values pour identifier les features les plus influentes par groupe.",
      },
    ],
    validation: "Rapport de biais genere avec DIR < 0.8 documente. Features problematiques identifiees. Recommandations de re-entrainement formulees.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'no-quiz-1',
  title: 'Quiz : IA Responsable',
  duration: 5,
  content: {
    questions: [
      {
        id: 'no-q1-1',
        question: "Qu'est-ce que l'explainability (explicabilite) d'un modele ML ?",
        type: 'single-choice',
        options: [
          "La capacite a documenter le code source du modele",
          "La capacite a comprendre POURQUOI le modele prend une decision specifique",
          "La precision du modele sur le dataset de test",
          "Le nombre de parametres du modele",
        ],
        correctAnswer: "La capacite a comprendre POURQUOI le modele prend une decision specifique",
        explanation: "L'explicabilite permet d'identifier quelles features ont influence une prediction. Outils : SHAP (Shapley values), LIME, Grad-CAM. Requis par la reglementation EU AI Act pour les systemes a haut risque.",
      },
      {
        id: 'no-q1-2',
        question: "Selon l'EU AI Act, un modele de recrutement automatise est classe dans quelle categorie de risque ?",
        type: 'single-choice',
        options: [
          "Risque minimal",
          "Risque limite",
          "Risque eleve",
          "Risque inacceptable",
        ],
        correctAnswer: "Risque eleve",
        explanation: "L'EU AI Act classe les systemes de recrutement automatise comme 'High Risk' (Annexe III). Ils sont soumis a des exigences strictes : audit de biais, transparence, supervision humaine et documentation detaillee.",
      },
      {
        id: 'no-q1-3',
        question: "Quelle approche AWS utilise pour l'IA responsable ?",
        type: 'multiple-choice',
        options: [
          "SageMaker Clarify pour la detection de biais",
          "Bedrock Guardrails pour les LLMs",
          "Amazon Rekognition Governance",
          "AWS Responsible AI principles documentation",
        ],
        correctAnswer: ["SageMaker Clarify pour la detection de biais", "Bedrock Guardrails pour les LLMs"],
        explanation: "AWS SageMaker Clarify detecte les biais dans les datasets et modeles. Amazon Bedrock Guardrails bloque les sorties dangereuses des LLMs. AWS a aussi publie ses principes d'IA responsable.",
      },
    ],
  } as QuizContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'Le Biais',
  subtitle: 'AWS AI Practitioner — IA responsable et detection de biais',
  learningObjectives: [
    "Comprendre les types de biais dans les modeles ML",
    "Calculer et interpreter le Disparate Impact Ratio",
    "Utiliser SageMaker Clarify pour l'audit de biais",
    "Comprendre les exigences de l'EU AI Act",
    "Distinguer les approches de mitigation des biais",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1],
};

export const neuralOverrideScenario: NeuralOverrideScenario = {
  id: 'neural-override',
  title: 'Neural Override',
  subtitle: "Quand l'IA discrimine — corriger sans masquer",
  certification: 'AWS AI Practitioner',
  coverImage: require('../../../assets/images/characters/nina_raoul_working.png'),
  characters: neuralOverrideCharacters,
  chapters: [chapter1],
};

export default neuralOverrideScenario;
